import { useCallback, useState } from 'react';
import { annotatedExportName } from '../utils/export-name.js';

/** The pieces ImageViewer holds and this hook needs to composite. */
export interface ImageExportSources {
  /** The rendered <img>. Read at natural size, not display size. */
  image: HTMLImageElement;
  /** The annotation layer, as a PNG data URL at the requested multiplier. */
  annotationLayer: (multiplier: number) => string;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read the annotation layer'));
    img.src = src;
  });
}

/**
 * Download an annotated image as a flattened PNG.
 *
 * Images were the one format with no export at all: you could mark up a photo
 * of a part in a meeting and had no way to send the result to anyone. PDFs and
 * PPTX go through useAnnotatedExport (pdf-lib); DOCX and XLSX export their own
 * edits from their editors.
 *
 * Always PNG, even from a JPEG source. The output is lossless and re-encoding a
 * photo to JPEG on every export would compound artefacts.
 */
/**
 * Record how the last export ended, where the state readout can show it.
 *
 * localStorage, so it survives the WebView teardown that Teams for Android
 * performs when an external activity takes the foreground -- a download is one.
 * Without this, "the button does nothing" and "the export ran and the download
 * was swallowed" are indistinguishable, which is the trap that cost two days on
 * the file picker.
 */
function noteExport(outcome: string): void {
  try {
    window.localStorage.setItem('annoteer.lastExport', outcome);
  } catch {
    // diagnostics must never break an export
  }
}

export function useAnnotatedImageExport(fileName: string | undefined) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportedName, setExportedName] = useState<string | null>(null);

  const exportAnnotatedImage = useCallback(
    async (sources: ImageExportSources) => {
      setIsExporting(true);
      setExportError(null);
      try {
        const { image, annotationLayer } = sources;
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        if (!width || !height) throw new Error('Image has no dimensions yet');

        // Re-render the annotations at the source image's resolution rather
        // than the viewport's. Fabric redraws the vectors at this multiplier,
        // so lines stay crisp instead of being upscaled pixels.
        const displayedWidth = image.width || width;
        const multiplier = width / displayedWidth;

        const out = document.createElement('canvas');
        out.width = width;
        out.height = height;
        const ctx = out.getContext('2d');
        if (!ctx) throw new Error('Could not get a 2D context');

        ctx.drawImage(image, 0, 0, width, height);
        const overlay = await loadImage(annotationLayer(multiplier));
        ctx.drawImage(overlay, 0, 0, width, height);

        const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Could not encode the PNG');

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const downloadName = annotatedExportName(fileName ?? 'image', 'png');
        a.download = downloadName;
        a.click();
        URL.revokeObjectURL(url);
        noteExport(`clicked ${downloadName}`);
        setExportedName(downloadName);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        noteExport('FAILED ' + message);
        console.error('Annotated image export failed:', err);
        setExportError(message);
      } finally {
        setIsExporting(false);
      }
    },
    [fileName],
  );

  return { isExporting, exportError, exportedName, exportAnnotatedImage };
}
